/**
 * ProjectBrowser - Modal dialog for browsing and loading projects
 *
 * Displays a tree view with collapsible folders.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppState } from '../contexts/AppContext';
import { projectService } from '../../serverClient/projectService';
import type { FileTreeNode } from '../../serverClient/projectService';

interface TreeNodeProps {
  node: FileTreeNode;
  depth: number;
  expandedPaths: Set<string>;
  onToggleExpand: (path: string) => void;
  onSelectProject: (path: string) => void;
}

function TreeNode({ node, depth, expandedPaths, onToggleExpand, onSelectProject }: TreeNodeProps) {
  const isExpanded = expandedPaths.has(node.path || 'root');
  const hasChildren = node.children && node.children.length > 0;

  const handleClick = () => {
    if (node.isDirectory) {
      onToggleExpand(node.path || 'root');
    } else {
      onSelectProject(node.path);
    }
  };

  return (
    <>
      <button
        onClick={handleClick}
        style={{
          ...styles.treeItem,
          paddingLeft: 16 + depth * 20
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.background = node.isDirectory ? '#374151' : '#3b82f6';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
        }}
      >
        {node.isDirectory && (
          <span style={styles.expandIcon}>
            {hasChildren ? (isExpanded ? '▼' : '▶') : ' '}
          </span>
        )}
        <span style={styles.icon}>
          {node.isDirectory ? '📁' : '📄'}
        </span>
        <span style={styles.itemName}>{node.name || 'projects'}</span>
      </button>

      {node.isDirectory && isExpanded && node.children && (
        <div style={styles.childrenContainer}>
          {node.children.map(child => (
            <TreeNode
              key={child.path}
              node={child}
              depth={depth + 1}
              expandedPaths={expandedPaths}
              onToggleExpand={onToggleExpand}
              onSelectProject={onSelectProject}
            />
          ))}
        </div>
      )}
    </>
  );
}

export function ProjectBrowser() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { isProjectBrowserOpen, closeProjectBrowser, logError } = useAppState();
  const [tree, setTree] = useState<FileTreeNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set(['root']));

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const projectTree = await projectService.listTree();
      setTree(projectTree);
      // Expand root by default
      setExpandedPaths(new Set(['root', '']));
      setLoading(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      logError('ProjectBrowser', `Error loading projects: ${msg}`);
      setError('Error loading projects: ' + msg);
      setLoading(false);
    }
  }, [logError]);

  useEffect(() => {
    if (!isProjectBrowserOpen) return;
    loadProjects();
  }, [isProjectBrowserOpen, loadProjects]);

  const handleToggleExpand = useCallback((path: string) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  const handleSelectProject = useCallback((path: string) => {
    // Extract just the project name from the path (remove directory and extension)
    const name = path.split('/').pop()?.replace(/\.json$/, '') ?? path;
    closeProjectBrowser();
    navigate(`/${sessionId}/project/${encodeURIComponent(name)}`);
  }, [closeProjectBrowser, navigate, sessionId]);

  if (!isProjectBrowserOpen) return null;

  const hasProjects = tree && tree.children && tree.children.length > 0;

  return (
    <div style={styles.overlay} onClick={closeProjectBrowser}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <span style={styles.title}>Load Project</span>
          <button onClick={closeProjectBrowser} style={styles.closeButton}>×</button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {loading && (
            <div style={styles.message}>Loading...</div>
          )}

          {error && (
            <div style={styles.error}>{error}</div>
          )}

          {!loading && !error && !hasProjects && (
            <div style={styles.message}>No saved projects</div>
          )}

          {!loading && !error && hasProjects && tree && (
            <div style={styles.treeContainer}>
              {tree.children!.map(child => (
                <TreeNode
                  key={child.path}
                  node={child}
                  depth={0}
                  expandedPaths={expandedPaths}
                  onToggleExpand={handleToggleExpand}
                  onSelectProject={handleSelectProject}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  modal: {
    background: '#1f2937',
    borderRadius: 12,
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
    width: 450,
    maxWidth: '90vw',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #374151',
    background: '#374151'
  },
  title: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    fontSize: 24,
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1
  },
  content: {
    padding: 8,
    overflowY: 'auto',
    flex: 1
  },
  message: {
    color: '#9ca3af',
    textAlign: 'center',
    padding: 20
  },
  error: {
    color: '#ef4444',
    textAlign: 'center',
    padding: 20
  },
  treeContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  treeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    background: 'transparent',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    transition: 'background 0.1s'
  },
  expandIcon: {
    width: 12,
    fontSize: 10,
    color: '#9ca3af',
    flexShrink: 0
  },
  icon: {
    fontSize: 16,
    flexShrink: 0
  },
  itemName: {
    color: 'white',
    fontSize: 14,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  childrenContainer: {
    display: 'flex',
    flexDirection: 'column'
  }
};
